
import { Component, Vue } from 'vue-property-decorator'
import { DeviceImportHistoryItem } from '@/data/models/DeviceImportHistoryItem'
import { getImportHistoryData } from '@/data/repository/GenericDeviceRepository'
import ImportHistoryItem from '@/components/settings/devices/import/ImportHistoryItem.vue'
import { ToolbarContentModule } from '@/store/modules/ToolbarContent'
import { ViewerSection } from '@/components/common/data-viewer/DataViewerModels'
import DataViewerComponent from '@/components/common/data-viewer/DataViewer.vue'
import { mapDeviceCsvToSection } from '@/utils/DeviceCsvToViewer'
import { CSV_REPORT_HEADERS, downloadReport } from '@/utils/devices.csv'
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from '@/utils/colors'
import { themed } from '@/utils/Theme'
import { calculateBackgroundStyle, setLastVisitedItem, StorageKeyLastVisitedItemLevel } from '@/utils/Utils'
import { DownloadFileType, getFileTypeIcon } from "@/utils/FileTypeIcons";

@Component({
  components: {
    ImportHistoryItem,
    "data-viewer": DataViewerComponent,
  },
})
export default class DeviceImportHistory extends Vue {

  iconResource = 'mdi-sort-calendar-descending'
  dateAscending = true
  importItemsList: DeviceImportHistoryItem[] = []
  showEmptyListMessage = false

  /** Currently selected history item for DataViewer display */
  selectedItem: DeviceImportHistoryItem | null = null
  viewerSections: ViewerSection[] = []
  lastVisitedKey = ''

  /** Table header background, theme-aware so it stays readable in dark mode. */
  /**
   * Icon of the file this button produces.
   *
   * An import report is a CSV, matching the CSV that was imported.
   */
  get downloadIcon(): string {
    return getFileTypeIcon(DownloadFileType.CSV);
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  async created() {
    const data = await getImportHistoryData()
    this.importItemsList = data.docs.map((doc) => doc.data()) as DeviceImportHistoryItem[]
    if (this.importItemsList.length <= 0) {
      this.showEmptyListMessage = true
    }
    this.changeSortOrder()
  }

  changeSortOrder() {
    if (this.showEmptyListMessage) {
      return
    }
    this.dateAscending = !this.dateAscending
    this.iconResource = this.dateAscending ? 'mdi-sort-calendar-descending' : 'mdi-sort-calendar-ascending'

    this.importItemsList.sort((a, b) => {
      const timestampA = a.timestamp.toMillis()
      const timestampB = b.timestamp.toMillis()
      return this.dateAscending ? timestampA - timestampB : timestampB - timestampA
    })
  }

  /** Opens a history item in the DataViewer. */
  openItem(item: DeviceImportHistoryItem) {
    this.lastVisitedKey = item.id;
    setLastVisitedItem(item.id, StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.selectedItem = item;
    this.viewerSections = [
      mapDeviceCsvToSection(
        CSV_REPORT_HEADERS,
        item.report,
        "report",
        item.fileName,
        true,
      ),
    ];
  }

  /** Closes the DataViewer and returns to the list. */
  closeViewer() {
    this.selectedItem = null;
    this.viewerSections = [];
  }

  /** Returns the last-visited background style for a given item. */
  getBackgroundStyle(itemId: string) {
    this.lastVisitedKey; // reactive dependency
    return calculateBackgroundStyle(itemId);
  }

  /** Downloads the currently viewed report as CSV. */
  onDownloadReport() {
    if (!this.selectedItem) return;
    downloadReport(
      document,
      this.selectedItem.fileName,
      this.selectedItem.report,
    );
  }

  mounted() {
    this.setToolbarTitle();
  }

  setToolbarTitle() {
    const baseTitle = this.$t("pages.settings").toString();
    const currentPageTitle = this.$t("settings.hyDryDevice.deviceImportHistory").toString();

    const toolbarTitle = baseTitle + " > " + currentPageTitle;

    ToolbarContentModule.setToolbarTitle(toolbarTitle);
  }
}
